import React, { useEffect, useState } from "react"
import { Container, Card, CardBody, Row, Col, Table, Button } from "reactstrap"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { getHrDashboard } from "../../services/Hrms/workforce"
import { toastError } from "../../helpers/msg_helper"

const Kpi = ({ label, value, color, onClick }) => (
  <Col>
    <Card className="text-center h-100" role={onClick ? "button" : undefined} onClick={onClick}>
      <CardBody className="py-3 px-2">
        <div className="text-muted text-uppercase" style={{ fontSize: 10.5, letterSpacing: ".02em", whiteSpace: "nowrap" }}>{label}</div>
        <h3 className="mt-2 mb-0" style={{ color }}>{value ?? "—"}</h3>
      </CardBody>
    </Card>
  </Col>
)

// A simple ranked list (label + count) with a proportional bar.
const RankTable = ({ title, rows, onRow }) => {
  const max = Math.max(1, ...(rows || []).map(r => r.count))
  return (
    <Card className="h-100">
      <CardBody>
        <h6 className="fw-semibold mb-3">{title}</h6>
        <Table borderless className="align-middle tnt-table mb-0">
          <tbody>
            {(rows || []).map((r, i) => (
              <tr key={i} role={onRow ? "button" : undefined} onClick={onRow ? () => onRow(r) : undefined}>
                <td style={{ width: "45%" }} className="text-truncate" title={r.label}>{r.label}</td>
                <td>
                  <div style={{ background: "#eef1f6", borderRadius: 4, height: 8 }}>
                    <div style={{ width: `${(r.count / max) * 100}%`, background: "#2f57c4", height: 8, borderRadius: 4 }} />
                  </div>
                </td>
                <td className="text-end fw-500" style={{ width: 56 }}>{r.count}</td>
              </tr>
            ))}
            {!rows?.length && <tr><td className="text-center text-muted">No data</td></tr>}
          </tbody>
        </Table>
      </CardBody>
    </Card>
  )
}

const HrDashboard = () => {
  const navigate = useNavigate()
  document.title = "HR Reports"
  const [d, setD] = useState(null)
  // company-based reports only make sense across a group; a single-company scope is one company
  const isGroup = useSelector(s => s.Company)?.scopeType === "group"

  useEffect(() => {
    ;(async () => {
      try { const r = await getHrDashboard(); setD(r?.data) }
      catch (e) { toastError(e?.message || "Failed to load HR reports") }
    })()
  }, [])

  const statusEntries = Object.entries(d?.statuses || {})

  return (
    <div className="page-content report-page">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center">
          <Breadcrumbs title="HRMS" breadcrumbObject={[
            { title: "HRMS", link: "/hrms" },
            { title: "HR Reports", link: "#" },
          ]} />
          <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
            <Button color="light" size="sm" onClick={() => navigate("/hrms-expiry-report")}>Expiry Report</Button>
            <Button color="light" size="sm" onClick={() => navigate("/hrms-workforce")}>Workforce</Button>
          </div>
        </div>

        <Row className="g-2 mb-2">
          <Kpi label="Total Employees" value={d?.total} color="#1f2937" onClick={() => navigate("/hrms-workforce")} />
          <Kpi label="In Camp" value={d?.housed} color="#1a9d5f" />
          <Kpi label="On Vacation" value={d?.on_vacation} color="#e69a16" onClick={() => navigate("/hrms-workforce?camp=VACATION")} />
          <Kpi label="Absconding" value={d?.absconding} color="#e74c3c" onClick={() => navigate("/hrms-workforce?camp=ABSCOND")} />
          <Kpi label="New Joiners (90d)" value={d?.new_joiners_90d} color="#2f57c4" />
          <Kpi label="Docs Expiring (60d)" value={d?.docs_expiring_60d} color="#e69a16" onClick={() => navigate("/hrms-expiry-report")} />
          <Kpi label="Docs Expired" value={d?.docs_expired} color="#e74c3c" onClick={() => navigate("/hrms-expiry-report")} />
        </Row>

        <Row className="g-2 mb-2">
          <Col lg={6}><RankTable title="By Nationality (top 10)" rows={d?.by_nationality} onRow={r => navigate(`/hrms-workforce?nationality=${encodeURIComponent(r.label)}`)} /></Col>
          <Col lg={6}><RankTable title="By Trade (top 10)" rows={d?.by_trade} onRow={r => navigate(`/hrms-workforce?trade=${encodeURIComponent(r.label)}`)} /></Col>
        </Row>

        <Row className="g-2 mb-2">
          {isGroup && (
            <Col lg={6}>
              <Card className="h-100">
                <CardBody>
                  <h6 className="fw-semibold mb-3">By Company</h6>
                  <Table borderless className="align-middle tnt-table mb-0">
                    <thead><tr><th>Company</th><th className="text-end">Employees</th></tr></thead>
                    <tbody>
                      {(d?.by_company || []).map((c, i) => (
                        <tr key={i} role="button" onClick={() => navigate(`/hrms-workforce?company_id=${c.id}`)}>
                          <td title={c.label}>{c.code}</td><td className="text-end fw-500">{c.count}</td>
                        </tr>
                      ))}
                      {!d?.by_company?.length && <tr><td colSpan="2" className="text-center text-muted">No data</td></tr>}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          )}
          <Col lg={isGroup ? 6 : 12}>
            <Card className="h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-semibold mb-0">
                    <span role="button" className="text-primary" onClick={() => navigate("/hrms-expiry-report")}>Document Expiry ›</span>
                  </h6>
                  <span className="text-muted small">click a number for the list · expired / ≤30d / ≤60d</span>
                </div>
                <Table borderless className="align-middle tnt-table mb-0">
                  <thead><tr><th>Document</th><th className="text-center text-danger">Expired</th><th className="text-center">≤30d</th><th className="text-center">≤60d</th></tr></thead>
                  <tbody>
                    {(d?.doc_by_type || []).map((t, i) => {
                      const cell = (val, bucket) => val
                        ? <span role="button" className="text-decoration-underline" onClick={() => navigate(isGroup ? `/hrms-expiry-report?type=${t.key}` : `/hrms-expiry-list?type=${t.key}&bucket=${bucket}`)}>{val}</span>
                        : 0
                      return (
                        <tr key={i}>
                          <td>{t.type}</td>
                          <td className="text-center fw-500" style={{ color: t.expired ? "#e74c3c" : undefined }}>{cell(t.expired, "expired")}</td>
                          <td className="text-center">{cell(t.d30, "d30")}</td>
                          <td className="text-center">{cell(t.d60, "d60")}</td>
                        </tr>
                      )
                    })}
                    {!d?.doc_by_type?.length && <tr><td colSpan="4" className="text-center text-muted">No expiry data</td></tr>}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Card>
          <CardBody>
            <h6 className="fw-semibold mb-3">Accommodation</h6>
            <Row>
              <Col md={6}>
                <div className="text-muted small text-uppercase mb-2" style={{ letterSpacing: ".04em" }}>In camp (by group)</div>
                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                  {Object.entries(d?.by_group || {}).map(([k, v]) => (
                    <span key={k} role="button" className="badge bg-light text-dark border" style={{ fontSize: 12, padding: "6px 10px" }}
                      onClick={() => navigate(`/hrms-workforce?camp=${encodeURIComponent(k)}`)}>
                      {k} <strong className="ms-1">{v}</strong>
                    </span>
                  ))}
                  {!Object.keys(d?.by_group || {}).length && <span className="text-muted">No data</span>}
                </div>
              </Col>
              <Col md={6}>
                <div className="text-muted small text-uppercase mb-2" style={{ letterSpacing: ".04em" }}>Not in camp (status)</div>
                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                  {statusEntries.map(([k, v]) => (
                    <span key={k} role="button" className="badge bg-light text-dark border" style={{ fontSize: 12, padding: "6px 10px" }}
                      onClick={() => navigate(`/hrms-workforce?camp=${encodeURIComponent(k)}`)}>
                      {k.charAt(0) + k.slice(1).toLowerCase()} <strong className="ms-1">{v}</strong>
                    </span>
                  ))}
                  {!statusEntries.length && <span className="text-muted">No data</span>}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

export default HrDashboard
